import { type ReactElement, type ReactNode, useEffect, useState } from "react"
import { type DashStyleValue } from "highcharts"
import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"
import SuperChart, { type CategoryChartSeries } from "./SuperChart"
import { sizeSplitGraphTooltipFormatter } from "./utils/GraphTooltipUtils"
import { useChartBaseColors } from "~/constants/colors"
import { ChartType } from "~/constants/graphs"
import { ceil, sumBy } from "lodash"

import { customSortComparatorByProperty, sizeSortingOrder } from "~/utils/sizeSplit"

type SeriesData = {
  name: string
  color?: string
  dashStyle?: DashStyleValue
  data: { size: string | null; value: number }[]
}

type Props = {
  title: string
  series: SeriesData[]
  className?: string
  headerContent?: ReactElement | null
  dataAsPercentage?: boolean
  alternativeContent?: ReactNode
}

export const SizeSplitGraph = ({
  title,
  series,
  className,
  headerContent,
  dataAsPercentage = true,
  alternativeContent,
}: Props) => {
  const chartBaseColors = useChartBaseColors()
  const [chartData, setChartData] = useState<CategoryChartSeries[]>([])
  const [seriesTotal, setSeriesTotal] = useState<Record<string, number>>({})
  const [categoryCharCount, setCategoryCharCount] = useState(0)

  useEffect(() => {
    const processedSeries: CategoryChartSeries[] = series.map((seriesItem) => {
      let total = 0
      const sizesArray = seriesItem.data.map((sku) => {
        const value = sku.value ?? 0
        total += value
        return { size: sku.size || "", value }
      })

      setSeriesTotal((prev) => ({
        ...prev,
        [seriesItem.name]: total,
      }))

      if (dataAsPercentage && total > 0) {
        sizesArray.forEach((sku) => (sku.value = sku.value / total))
      }

      sizesArray.sort(customSortComparatorByProperty("size", sizeSortingOrder))

      const categories = sizesArray.map((s) => s.size)

      setCategoryCharCount(sumBy(categories, (c) => c.length))

      return {
        name: seriesItem.name,
        type: "areaspline",
        data: categories.map((category, index) => ({
          category,
          value: Math.round((sizesArray[index]?.value ?? 0) * 1000) / 1000,
        })),
        colors: seriesItem.color ? [seriesItem.color] : [chartBaseColors.base],
        dashStyle: seriesItem.dashStyle,
      }
    })

    setChartData(processedSeries)
  }, [series, dataAsPercentage, chartBaseColors])

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="whitespace-nowrap">{title}</CardTitle>
        {headerContent && <div className="flex items-center gap-1.5">{headerContent}</div>}
      </CardHeader>
      <CardContent className={alternativeContent ? undefined : "h-60"}>
        {alternativeContent ?? (
          <SuperChart
            key={dataAsPercentage ? "pct" : "units"}
            type={ChartType.CategorySeries}
            data={chartData}
            height={210}
            compact
            noLegend={chartData.length === 1}
            xAxisLabelSteps={ceil((categoryCharCount ?? 1) / 20)} // 30 is the max number of characters that can be displayed
            dataAsPercentage={dataAsPercentage}
            customTooltipFormatter={function (this: Highcharts.Point) {
              return sizeSplitGraphTooltipFormatter.call(this, dataAsPercentage, seriesTotal)
            }}
          />
        )}
      </CardContent>
    </Card>
  )
}
